import { useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";
import { uploadDroppedItems } from "utils/upload/uploadDroppedItems";

import { IShapefileNode } from "models/projects";

import { EditableShapefileFolderNode } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager";
import { useStructuredNodeContext } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/StructuredNodeContext";
import { setInEditMode } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/reducer";
import {
  FlexItem,
  LeafTitle
} from "components/project/layers/components/layer-managers/shared";
import { useRightClickContextMenu } from "components/project/layers/components/layer-managers/shared/hooks/useRightClickContextMenu";
import { ShapefileFolderActions } from "components/project/layers/components/layer-managers/shared/shapefile-folder-actions/ShapefileFolderActions";
import { SHAPEFILE_FOLDER_TYPE } from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useProjectDispatch } from "components/project/projects/context";
import { useCreateProjectShapefileFolder } from "components/project/shapefiles/hooks/createProjectShapefileFolder/useCreateProjectShapefileFolder";
import { useUploadProjectShapefileList } from "components/project/shapefiles/hooks/useUploadProjectShapefileList";
import { useDeleteProjectShapefileFolderMutation } from "components/project/shapefiles/mutations";

export default function OrgFolderNode({ node }) {
  const projectDispatch = useProjectDispatch();

  const [{ inEditMode }, nodeDispatch] = useStructuredNodeContext();
  const [canDrop, setCanDrop] = useState(false);
  const [tempFoldersLength, setTempFoldersLength] = useState(0);
  const role = useSelector((state: RootState) => state.auth.user.role);
  const isAdminOrPowerUser = role === "Admin" || role === "PowerUser";

  const { openContextMenu, contextMenuObject } = useRightClickContextMenu();

  const createProjectFolder = useCreateProjectShapefileFolder({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const deleteFolder = useDeleteProjectShapefileFolderMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const uploadShapefileList = useUploadProjectShapefileList({
    visibility: "organization",
    projectId: ""
  });

  const handleAddFolder = async () => {
    setTempFoldersLength((prev) => prev + 1);

    try {
      await createProjectFolder.mutateAsync({
        parentId: sf.shapefileNodeId,
        order: node.children.length + tempFoldersLength
      });
    } finally {
      setTempFoldersLength((prev) => prev - 1);
    }
  };

  const sf = node as IShapefileNode;
  return (
    <LeafTitle
      canDrop={canDrop}
      onContextMenu={openContextMenu}
      onDragOver={(e) => {
        e.preventDefault();
        const files = getFilesFromEvent(e);
        setCanDrop(files.length > 0);
      }}
      // A dropped folder has to be read before it can be uploaded, and the browser
      // empties the dropped list as soon as this handler returns, so the list is
      // handed over before the first await
      onDrop={async (ev) => {
        ev.preventDefault();
        setCanDrop(false);
        await uploadDroppedItems({
          transfer: ev.dataTransfer,
          shapefileNodeId: sf.shapefileNodeId,
          upload: uploadShapefileList.mutate
        });
      }}
      onDragEnter={(e) => {
        const files = getFilesFromEvent(e);
        setCanDrop(files.length > 0);
      }}
      onDragLeave={() => {
        setCanDrop(false);
      }}>
      <EditableShapefileFolderNode
        projectId={SHAPEFILE_FOLDER_TYPE.ORGANIZATION}
        node={node}
      />
      {!inEditMode && (
        <FlexItem>
          <ShapefileFolderActions
            isDisabled={!isAdminOrPowerUser}
            hasSelectItems={false} // Only available in project tree
            hasColorPicker={isAdminOrPowerUser}
            projectId={SHAPEFILE_FOLDER_TYPE.ORGANIZATION}
            addFolder={handleAddFolder}
            place="left"
            deleteFolder={() => {
              deleteFolder.mutate({
                shapefileNodeId: sf.shapefileNodeId,
                folderNode: node
              });
            }}
            setIsRenaming={() => {
              setInEditMode(nodeDispatch, true, (isDraggable) =>
                projectDispatch({ payload: { isTreeDraggable: isDraggable } })
              );
            }}
            folderName={node.title}
            shapefileNodeId={sf.shapefileNodeId}
            contextMenu={contextMenuObject}
          />
        </FlexItem>
      )}
    </LeafTitle>
  );
}
